import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import ArrowRightAltOutlinedIcon from "@mui/icons-material/ArrowRightAltOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import DatePicker from "react-datepicker";
import "./css/react-datepicker.css";
import "./css/date-picker.css";

const timeSelection = {
  options: [
    {
      value: "1",
      label: "1 minute"
    },
    {
      value: "5",
      label: "5 minutes"
    },
    {
      value: "15",
      label: "15 minutes"
    },
    {
      value: "30",
      label: "30 minutes"
    },
    {
      value: "60",
      label: "Past 1 hour"
    },
    {
      value: "120",
      label: "Past 2 hours"
    },
    {
      value: "240",
      label: "Past 4 hours"
    },
    {
      value: "480",
      label: "Past 8 hours"
    },
    {
      value: "1440",
      label: "Past day"
    },
    {
      value: "10080",
      label: "Past week"
    },
    {
      value: "43200",
      label: "Past month"
    },
    {
      value: "custom",
      label: "Custom"
    }
  ]
};

const LogsFilter = ({ setFilterDate, onSearchChange, canFilter }) => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const { isLoadingLogs } = useSelector((store) => store?.portal?.logs);
  const [isDateInvalid, setIsDateInvalid] = useState(false);
  const [selectedTimeOption, setSelectedTimeOption] = useState(timeSelection?.options[2]);

  const handleTimeSelection = (option) => {
    setSelectedTimeOption(option);
    if (option.value !== "custom") {
      setStartDate(new Date());
      setEndDate(new Date());
    }
  };

  const handleFilterStartTime = (time) => {
    const timeDate = new Date(time);
    return timeDate.getTime() <= endDate.getTime();
  };

  const handleFilterEndTime = (time) => {
    const timeDate = new Date(time);
    return timeDate.getTime() >= startDate.getTime();
  };

  const handleFilterStartEndDatetime = () => {
    if (isDateInvalid) return;

    if (selectedTimeOption?.value === "custom") {
      setFilterDate({
        startDateTime: startDate.toISOString(),
        endDateTime: endDate.toISOString()
      });
    } else {
      const end = new Date();
      const start = new Date(end - parseInt(selectedTimeOption?.value) * 60 * 1000);
      setFilterDate({
        startDateTime: start.toISOString(),
        endDateTime: end.toISOString()
      });
    }
  };

  useEffect(() => {
    setIsDateInvalid(startDate > endDate);
  }, [startDate, endDate]);

  return (
    <SoftBox width={{ xs: "100%" }}>
      <SoftTypography variant="h6" mb={1}>
        Sort by
      </SoftTypography>
      <Grid container spacing={2} wrap="wrap" alignItems="center">
        <Grid size={{ xs: 12, md: 8, lg: 7 }}>
          <SoftBox
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "flex-start",
              flexWrap: { xs: "wrap", md: "nowrap" }
            }}
          >
            <SoftBox width={{ xs: "100%", md: "50%" }}>
              <SoftSelect
                placeholder="Choose time"
                defaultValue={selectedTimeOption}
                options={timeSelection?.options}
                onChange={handleTimeSelection}
                isDisabled={isLoadingLogs}
              />
            </SoftBox>
            {selectedTimeOption?.value === "custom" && (
              <SoftBox
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 1,
                  alignItems: "center",
                  order: { xs: 1, md: 0 },
                  width: "100%"
                }}
              >
                <SoftBox
                  sx={{
                    border: `1px solid ${isDateInvalid ? "#fd5e5e" : "transparent"}`,
                    borderRadius: "8px",
                    width: { xs: "100%", md: "40%" }
                  }}
                >
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={30}
                    timeCaption="Time"
                    dateFormat="MMMM d, yyyy h:mm aa"
                    customInput={<SoftInput />}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="scroll"
                    disabled={isLoadingLogs}
                    maxDate={endDate}
                    filterTime={handleFilterStartTime}
                  />
                </SoftBox>
                <SoftBox sx={{ display: "flex", alignItems: "center" }}>
                  <ArrowRightAltOutlinedIcon />
                </SoftBox>
                <SoftBox
                  sx={{
                    border: `1px solid ${isDateInvalid ? "#fd5e5e" : "transparent"}`,
                    borderRadius: "8px",
                    width: { xs: "100%", md: "40%" }
                  }}
                >
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={30}
                    timeCaption="Time"
                    dateFormat="MMMM d, yyyy h:mm aa"
                    customInput={<SoftInput />}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="scroll"
                    disabled={isLoadingLogs}
                    minDate={startDate}
                    filterTime={handleFilterEndTime}
                  />
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          lg={5}
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: { xs: "flex-start", md: "flex-end" },
            alignItems: "center"
          }}
        >
          <SoftBox
            sx={{
              position: "relative",
              width: { xs: "150px", md: "auto" }
            }}
          >
            <SoftButton
              color="info"
              onClick={handleFilterStartEndDatetime}
              sx={{ minWidth: "150px" }}
              disabled={isLoadingLogs || isDateInvalid}
            >
              Search
              {isLoadingLogs && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "#fff",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                    zIndex: 1
                  }}
                />
              )}
            </SoftButton>
          </SoftBox>
          {canFilter && (
            <SoftInput
              placeholder="Search..."
              onChange={(e) => onSearchChange(e.target.value)}
              disabled={isLoadingLogs}
              sx={{
                width: { xs: "100%", md: "200px" }
              }}
            />
          )}
        </Grid>
      </Grid>
    </SoftBox>
  );
};

export default LogsFilter;
